//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { PrimaryLinkButton } from '@components/atoms/button/Button';
import Icon from '@components/atoms/icon/Icon';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import { BodyText } from '@components/atoms/text/Text';
import VisuallyHiddenText from '@components/atoms/visually-hidden-text/VisuallyHiddenText';
import {
  FilterOption,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { SELECTED_PRODUCTS_LIMIT } from '@components/media-filters/mediaFilterConstants';
import { DEFAULT_PAGE_NUMBER } from '@components/molecules/pagination/TablePaginationControls';
import { SearchBarWithTypeAhead } from '@components/molecules/search-bar-with-type-ahead/SearchBarWithTypeAhead';
import { ContentDrawerTrackingIds } from '@content-drawer/contentDrawerConstants';
import { useProductsQuery } from '@content-drawer/filters/labels/hooks/useProductsQuery';
import { MediaLibraryTrackingIds } from '@media-library/constants/mediaLibraryTrackingIds';
import { useProductCatalogsQuery } from '@uia/products/hooks/useProductCatalogsQuery';
import { isDefined } from '@utils/filterPredicates';

export const DEFAULT_ITEMS_PER_PAGE = 5;

interface ProductsTypeaheadSearchProps {
  openModal?: () => void;
  searchTerm: string;
  selectProduct: (product: FilterOption<FilterValue>) => void;
  selectedProductIds: string[];
  setSearchTerm: (term: string) => void;
}

export const ProductsTypeaheadSearch = ({
  openModal,
  searchTerm,
  selectProduct,
  selectedProductIds,
  setSearchTerm,
}: ProductsTypeaheadSearchProps) => {
  const { data: catalogs } = useProductCatalogsQuery();

  const { data: searchResult, isPending } = useProductsQuery({
    hasCatalog: isDefined(catalogs) && catalogs.length > 0,
    limit: DEFAULT_ITEMS_PER_PAGE,
    offset: DEFAULT_PAGE_NUMBER,
    search: searchTerm,
  });

  const productLimitReached =
    selectedProductIds.length >= SELECTED_PRODUCTS_LIMIT;

  return (
    <StyledSearchBarWithTypeAhead
      ellipsizeSubTitle={1}
      footer={
        (searchResult?.totalCount ?? 0) > DEFAULT_ITEMS_PER_PAGE && (
          <StyledPrimaryLinkButton
            onClick={openModal}
            trackingId={MediaLibraryTrackingIds.PRODUCTS_VIEW_ALL_BUTTON}
          >
            View More Results
          </StyledPrimaryLinkButton>
        )
      }
      getTypeAheadResults={async (searchTerm) => {
        if (isDefined(searchTerm)) {
          setSearchTerm(searchTerm);
        }
      }}
      isDisabled={productLimitReached}
      isLoadingTypeAheadResults={isPending}
      onSearchSelection={async (selectedProduct) => {
        if (isDefined(selectedProduct.id)) {
          selectProduct({
            isSelected: true,
            label: selectedProduct.label,
            value: selectedProduct.id,
          });
        }
      }}
      searchBarPlaceholderText='Name, SKU, URL'
      searchBarTooltip={
        productLimitReached
          ? 'Maximum 10 product tags allowed for search.'
          : undefined
      }
      searchPrefixes={[]}
      shouldEnableSearchTermFiltering={false}
      shouldShowHeader={false}
      trackingIdPrefix={ContentDrawerTrackingIds.SEARCH_PRODUCTS}
      typeAheadResults={
        searchResult?.products.map((product, index) => ({
          count: index,
          header: '',
          id: product.productStyleId,
          label: product.name ?? '',
          labelSubtitle: product.externalId,
          postfixIcon:
            product.styleId && selectedProductIds.includes(product.styleId) ? (
              <>
                <SelectIndicatorIcon />
                <VisuallyHiddenText>Selected</VisuallyHiddenText>
              </>
            ) : undefined,
          prefixIcon: (
            <StyledImage
              alt={product.name ?? 'Product Image'}
              src={product.images[0]?.imageUrl}
            />
          ),
          shouldShowCount: false,
          styledLabel: ({ children }: { children: ReactNode }) => (
            <BodyText ellipsize={1} title={product.name}>
              {children}
            </BodyText>
          ),
        })) ?? []
      }
    />
  );
};

const StyledImage = styled(ImageWithErrorHandling)(
  ({ theme }) => `
    width: ${theme.spacings[6]};
    height: ${theme.spacings[6]};
    object-fit: contain;
  `,
);

const StyledSearchBarWithTypeAhead = styled(SearchBarWithTypeAhead)(
  ({ theme }) => `
    position: inherit;

    [data-tippy-root] {
      width: calc(${theme.spacings[4]} * 13);
    }
  `,
);

const StyledPrimaryLinkButton = styled(PrimaryLinkButton)(
  ({ theme }) => `
    padding: ${theme.spacings[2]};
  `,
);

const SelectIndicatorIcon = styled(Icon).attrs({
  name: 'checkmark',
})(
  ({ theme }) => css`
    color: ${theme.colors.green7};
    margin-left: ${theme.spacings[1]};
  `,
);
